import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { UserContext } from 'base'
import { useContext, useState } from 'react'
import ButtonWithLoading from './button_with_loading'
import { UnauthorisedError } from 'errors'
import { UserPermission } from 'models'

interface PermissionSelectorProps {
  selectedPermissions: Array<UserPermission>
  handlePermissionToggle: (permission: UserPermission) => void
}

export const PermissionSelector = ({
  selectedPermissions,
  handlePermissionToggle,
}: PermissionSelectorProps) => {
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Permissions:
      </Typography>
      <FormGroup>
        {Object.values(UserPermission).map((permission) => (
          <FormControlLabel
            key={permission}
            control={
              <Checkbox
                checked={selectedPermissions.includes(permission)}
                onChange={() => handlePermissionToggle(permission)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {permission.replace('-bot', '').replace('-', ' ')}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </Box>
  )
}

interface TelegramUserFormProps {
  onSubmit: (
    userName: string,
    userFirstName: string,
    userTelegramUsername: string,
    selectedPermissions: Array<UserPermission>
  ) => Promise<void>
}

export const TelegramUserForm = ({ onSubmit }: TelegramUserFormProps) => {
  const [userName, setUserName] = useState<string>('')
  const [userFirstName, setUserFirstName] = useState<string>('')
  const [userTelegramUsername, setUserTelegramUsername] = useState<string>('')
  const [selectedPermissions, setSelectedPermissions] = useState<
    Array<UserPermission>
  >([])

  const [isSaving, setIsSaving] = useState<boolean>(false)
  const user = useContext(UserContext)

  const handleUserName = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUserName(event.target.value)
  }

  const handleUserFirstName = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUserFirstName(event.target.value)
  }

  const handleUserTelegramUsername = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUserTelegramUsername(event.target.value)
  }

  const handlePermissionToggle = (permission: UserPermission) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    )
  }

  return (
    <Box
      component="form"
      onSubmit={async (event) => {
        setIsSaving(true)
        event.preventDefault()
        try {
          await onSubmit(
            userName,
            userFirstName,
            userTelegramUsername,
            selectedPermissions
          )
        } catch (e: any) {
          if (e instanceof UnauthorisedError) {
            user.setLoggedOut()
          }
        }
        setIsSaving(false)
      }}
    >
      <Stack spacing={3}>
        <TextField
          required
          label="Name"
          fullWidth
          onChange={handleUserName}
          value={userName}
        />
        <TextField
          required
          label="First Name"
          fullWidth
          onChange={handleUserFirstName}
          value={userFirstName}
        />
        <TextField
          required
          label="Telegram Username"
          fullWidth
          onChange={handleUserTelegramUsername}
          value={userTelegramUsername}
        />
        <PermissionSelector
          selectedPermissions={selectedPermissions}
          handlePermissionToggle={handlePermissionToggle}
        />
        <Stack direction="row" justifyContent="right">
          <ButtonWithLoading
            type="submit"
            variant="contained"
            isLoading={isSaving}
          >
            Add
          </ButtonWithLoading>
        </Stack>
      </Stack>
    </Box>
  )
}
